import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { tokens } from '@/theme/tokens';
import type { ModalProps, ModalBodyProps, ModalFooterProps } from './Modal.types';

/**
 * # Modal
 *
 * Univerzalni modalni okno postavene na Headless UI Dialog.
 *
 * @example
 * <Modal isOpen={isOpen} onClose={close} title="Editace">
 *   <Modal.Body>Obsah...</Modal.Body>
 *   <Modal.Footer>...</Modal.Footer>
 * </Modal>
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  initialFocus,
  children,
  className,
  'data-testid': testId,
}: ModalProps) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
    wizard: 'max-w-5xl',
    full: 'max-w-full m-4',
  }[size];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={cn('relative', tokens.z.modal)}
        onClose={onClose}
        initialFocus={initialFocus}
        data-testid={testId}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn(
                  'w-full transform overflow-hidden text-left align-middle',
                  tokens.radius.lg,
                  'bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 shadow-2xl',
                  tokens.transition.normal,
                  sizeClasses,
                  className,
                )}
              >
                {title && (
                  <div className="flex items-center justify-between border-b border-emerald-100/70 dark:border-white/10 px-6 py-4">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900 dark:text-white"
                    >
                      {title}
                    </Dialog.Title>
                    <button
                      type="button"
                      className={cn(
                        'p-1.5 text-gray-500 hover:text-emerald-600 dark:text-zinc-400 dark:hover:text-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30',
                        tokens.radius.sm,
                        tokens.transition.fast,
                      )}
                      onClick={onClose}
                      title="Zavřít modal"
                      aria-label="Zavřít modal"
                    >
                      <span className="sr-only">Zavřít</span>
                      <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                )}

                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// --- Sub-components ---

const ModalBody = ({ children, noPadding, className, 'data-testid': testId }: ModalBodyProps) => {
  return (
    <div
      className={cn(
        !noPadding && 'px-6 py-4',
        'text-sm md:text-base text-gray-600 dark:text-zinc-300',
        className,
      )}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

const ModalFooter = ({ children, className, 'data-testid': testId }: ModalFooterProps) => {
  return (
    <div
      className={cn(
        'bg-emerald-50/55 dark:bg-white/5 px-6 py-4 flex flex-row-reverse gap-3 border-t border-emerald-100/70 dark:border-white/10',
        className,
      )}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
